/**
 * Everything around an open plan, so the editor (B6) only has to edit a document:
 *
 * - loads the plan, and offers "Restore unsaved changes?" when this computer has a newer copy;
 * - runs autosave (save indicator, offline retries, versions on close);
 * - the top bar: back to My plans, plan name (click to rename), save indicator, History button,
 *   plus the editor's own buttons via `toolbar`;
 * - the "also editing" banner and the "you saved over your friend's changes" notice;
 * - the History panel; restoring a version swaps in the restored doc.
 *
 * The editor is the `children` render function. It gets a `PlanContext`: start from `ctx.doc`,
 * call `ctx.commit(newDoc)` after each committed edit. When a version is restored, `ctx.doc` is
 * replaced and the children are re-mounted (keyed by `ctx.loadKey`), so the editor starts fresh.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as api from '../../api';
import type { GetPlanResponse, PlanDoc, PlanId, PlanMeta } from '../../../shared/types';
import {
  createAutosave,
  discardUnsavedCopy,
  findUnsavedCopy,
  statusLabel,
  useAutosaveStatus,
  type Autosave,
  type UnsavedCopy,
} from '../../store/autosave';
import { currentName } from '../../store/session';
import { Dialog, PromptDialog, errorText } from './dialogs';
import { clock, timeAgo } from './format';
import { HistoryPanel } from './HistoryPanel';
import { PlanSketch, planSummary } from './PlanSketch';
import { PresenceBanner, joinNames, usePresence } from './presence';
import { Link } from './router';

/** What the editor gets from the frame. */
export interface PlanContext {
  planId: PlanId;
  /** The plan's meta as last known (name follows renames). */
  meta: PlanMeta;
  /** The document to start from. Replaced (with a new `loadKey`) when a version is restored. */
  doc: PlanDoc;
  /** Changes whenever `doc` is replaced; the children are keyed by it. */
  loadKey: number;
  /** Hand the frame the new current document after a committed edit; it is saved automatically. */
  commit(doc: PlanDoc): void;
  /** The current document (the last one committed). */
  getDoc(): PlanDoc;
  /** The plan's autosave (flush before actions that need the server to have the latest doc). */
  autosave: Autosave;
  /** Opens the History panel. */
  openHistory(): void;
  /** The signed-in person's name. */
  me: string;
}

export interface PlanFrameProps {
  planId: PlanId;
  /** The editor. */
  children: (ctx: PlanContext) => ReactNode;
  /** Extra buttons for the top bar (Undo, Download picture, Simple/Advanced…). */
  toolbar?: (ctx: PlanContext) => ReactNode;
  /** Renders the 320 px card picture for My plans (PNG). Uploaded after saves, at most once a minute. */
  getThumbnail?: (doc: PlanDoc) => Promise<Blob | null>;
  /** Draws version previews in History; defaults to a simple sketch. */
  renderPreview?: (doc: PlanDoc) => ReactNode;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string; missing: boolean }
  | { kind: 'askRestore'; plan: GetPlanResponse; copy: UnsavedCopy }
  | { kind: 'ready' };

interface Start {
  doc: PlanDoc;
  baseRev: number;
  /** Start with unsaved changes (a restored local copy): save them straight away. */
  dirty: boolean;
}

/** The frame around an open plan; see the file comment. */
export function PlanFrame({ planId, children, toolbar, getThumbnail, renderPreview }: PlanFrameProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [meta, setMeta] = useState<PlanMeta | null>(null);
  const [start, setStart] = useState<Start | null>(null);
  const [startDoc, setStartDoc] = useState<PlanDoc | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [autosave, setAutosave] = useState<Autosave | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [presenceHidden, setPresenceHidden] = useState(false);
  const docRef = useRef<PlanDoc | null>(null);
  const thumbRef = useRef(getThumbnail);
  thumbRef.current = getThumbnail;

  const begin = useCallback((m: PlanMeta, s: Start) => {
    docRef.current = s.doc;
    setMeta(m);
    setStartDoc(s.doc);
    setStart(s);
    setPhase({ kind: 'ready' });
  }, []);

  const load = useCallback(async () => {
    setPhase({ kind: 'loading' });
    try {
      const plan = await api.getPlan(planId);
      const copy = await findUnsavedCopy(plan.meta, plan.doc);
      if (copy) setPhase({ kind: 'askRestore', plan, copy });
      else begin(plan.meta, { doc: plan.doc, baseRev: plan.meta.rev, dirty: false });
    } catch (e) {
      const missing = e instanceof api.ApiFailure && e.code === 'not_found';
      setPhase({
        kind: 'failed',
        missing,
        message: missing ? "This plan doesn't exist any more. It may have been deleted." : errorText(e),
      });
    }
  }, [planId, begin]);

  useEffect(() => {
    void load();
  }, [load]);

  // One autosave per start; closing it (leaving the page) saves and keeps a version. The close is
  // deferred a tick so an effect re-run without a real unmount (StrictMode, hot reload) keeps the
  // same autosave instead of starting a second one from an out-of-date rev.
  const live = useRef<{ start: Start; autosave: Autosave; closeTimer?: ReturnType<typeof setTimeout> } | null>(null);
  useEffect(() => {
    if (!start) return;
    let entry = live.current;
    if (entry && entry.start === start) {
      clearTimeout(entry.closeTimer);
    } else {
      if (entry) {
        clearTimeout(entry.closeTimer);
        void entry.autosave.close();
      }
      const a = createAutosave(planId, () => docRef.current!, start.baseRev, {
        getThumbnail: () => (thumbRef.current && docRef.current ? thumbRef.current(docRef.current) : Promise.resolve(null)),
      });
      entry = { start, autosave: a };
      live.current = entry;
      setAutosave(a);
      if (start.dirty) a.notifyChange();
    }
    const mine = entry;
    return () => {
      mine.closeTimer = setTimeout(() => {
        if (live.current === mine) live.current = null;
        void mine.autosave.close();
      }, 0);
    };
  }, [planId, start]);

  const others = usePresence(phase.kind === 'ready' ? planId : null);

  const ctx = useMemo<PlanContext | null>(() => {
    if (!meta || !startDoc || !autosave) return null;
    return {
      planId,
      meta,
      doc: startDoc,
      loadKey,
      commit(doc) {
        docRef.current = doc;
        autosave.notifyChange();
      },
      getDoc: () => docRef.current!,
      autosave,
      openHistory: () => setHistoryOpen(true),
      me: currentName(),
    };
  }, [planId, meta, startDoc, loadKey, autosave]);

  const onRestored = useCallback(
    async (plan: GetPlanResponse) => {
      if (!autosave) return;
      docRef.current = plan.doc;
      await autosave.rebase(plan.meta.rev);
      setMeta(plan.meta);
      setStartDoc(plan.doc);
      setLoadKey((k) => k + 1);
    },
    [autosave],
  );

  if (phase.kind === 'loading') {
    return <main className="page"><p className="muted">Opening the plan…</p></main>;
  }
  if (phase.kind === 'failed') {
    return (
      <main className="page">
        <div className="empty">
          <h2>{phase.missing ? 'Plan not found' : "Couldn't open the plan"}</h2>
          <p>{phase.message}</p>
          <div className="row center">
            <Link to="/" className="button">Back to My plans</Link>
            {!phase.missing && <button type="button" className="primary" onClick={() => void load()}>Try again</button>}
          </div>
        </div>
      </main>
    );
  }
  if (phase.kind === 'askRestore') {
    const { plan, copy } = phase;
    return (
      <main className="page">
        <RestoreUnsavedDialog
          copy={copy}
          meta={plan.meta}
          onRestore={() => begin(plan.meta, { doc: { ...copy.doc, name: plan.meta.name }, baseRev: copy.baseRev, dirty: true })}
          onDiscard={async () => {
            await discardUnsavedCopy(planId);
            begin(plan.meta, { doc: plan.doc, baseRev: plan.meta.rev, dirty: false });
          }}
        />
      </main>
    );
  }
  if (!ctx || !autosave || !meta) return null;

  return (
    <div className="plan-frame">
      <header className="plan-bar">
        <Link to="/" className="back" aria-label="Back to My plans">← My plans</Link>
        <button type="button" className="plan-name" title="Rename" onClick={() => setRenaming(true)}>
          {meta.name}
        </button>
        <SaveIndicator autosave={autosave} />
        <div className="plan-tools">{toolbar?.(ctx)}</div>
        <button type="button" onClick={() => setHistoryOpen(!historyOpen)} aria-pressed={historyOpen}>
          History
        </button>
      </header>
      {!presenceHidden && <PresenceBanner others={others} onDismiss={() => setPresenceHidden(true)} />}
      <OverwroteNotice autosave={autosave} others={others} onOpenHistory={() => setHistoryOpen(true)} />
      <div className="plan-body">
        <div className="plan-main" key={loadKey}>
          {children(ctx)}
        </div>
        {historyOpen && (
          <HistoryPanel
            planId={planId}
            autosave={autosave}
            onRestored={onRestored}
            onClose={() => setHistoryOpen(false)}
            renderPreview={renderPreview}
          />
        )}
      </div>
      {renaming && (
        <PromptDialog
          title="Rename plan"
          label="Name"
          initial={meta.name}
          confirmLabel="Rename"
          onClose={() => setRenaming(false)}
          onSubmit={async (name) => {
            const m = await api.renamePlan(planId, name);
            if (docRef.current) docRef.current = { ...docRef.current, name: m.name };
            setMeta(m);
          }}
        />
      )}
    </div>
  );
}

/** "Saved" / "Saving…" / "Offline, will retry", with details on hover and "Try now" when stuck. */
export function SaveIndicator({ autosave }: { autosave: Autosave }) {
  const s = useAutosaveStatus(autosave);
  const [, tick] = useState(0);
  useEffect(() => {
    if (s.state !== 'offline') return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [s.state]);

  // Warn before closing the tab only when changes can't reach the server.
  useEffect(() => {
    if (s.state !== 'offline' && s.state !== 'error' && s.state !== 'signedOut') return;
    const onBefore = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [s.state]);

  const title =
    s.state === 'saved' && s.lastSavedAt
      ? `Saved at ${clock(s.lastSavedAt)}`
      : s.state === 'offline' && s.retryAt
        ? `Trying again in ${Math.max(1, Math.ceil((s.retryAt - Date.now()) / 1000))} s. Your changes are kept on this computer meanwhile.`
        : (s.message ?? undefined);
  const stuck = s.state === 'offline' || s.state === 'error';
  return (
    <span className={`save-status ${s.state}`} title={title} role="status" aria-live="polite">
      <span className="dot" aria-hidden="true" />
      {statusLabel(s)}
      {s.state === 'error' && s.message && <span className="save-msg">{s.message}</span>}
      {stuck && (
        <button type="button" className="link" onClick={() => void autosave.flush().catch(() => undefined)}>
          Try now
        </button>
      )}
    </span>
  );
}

function OverwroteNotice({ autosave, others, onOpenHistory }: { autosave: Autosave; others: string[]; onOpenHistory: () => void }) {
  const s = useAutosaveStatus(autosave);
  if (!s.overwrote) return null;
  const who = others.length ? joinNames(others) : 'Someone else';
  return (
    <div className="banner warn" role="status">
      <span aria-hidden="true">⚠️</span>
      <span>
        {who} had saved changes to this plan, and your save replaced them. Their version is kept in History.
      </span>
      <button type="button" onClick={onOpenHistory}>Open History</button>
      <button type="button" onClick={() => autosave.acknowledgeOverwrite()}>OK</button>
    </div>
  );
}

interface RestoreProps {
  copy: UnsavedCopy;
  meta: PlanMeta;
  onRestore: () => void;
  onDiscard: () => void | Promise<void>;
}

/** "Restore unsaved changes?" shown when this computer has changes the server never got. */
export function RestoreUnsavedDialog({ copy, meta, onRestore, onDiscard }: RestoreProps) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  return (
    <Dialog title="Restore unsaved changes?" onClose={() => undefined} wide>
      <div className="dialog-body">
        <p>
          This computer has changes to <strong>{meta.name}</strong> from {timeAgo(copy.editedAt)} that didn't reach the
          planner (maybe the connection dropped or the page was closed).
        </p>
        {copy.serverChangedSince && (
          <p className="note">
            {meta.updatedBy || 'Someone'} saved this plan {timeAgo(meta.updatedAt)}, after these changes were made. If you
            restore, their version is kept in History.
          </p>
        )}
        <div className="compare">
          <figure>
            <PlanSketch doc={copy.doc} />
            <figcaption>Your unsaved changes<br /><span className="muted small">{planSummary(copy.doc)}</span></figcaption>
          </figure>
        </div>
      </div>
      <div className="dialog-actions">
        {confirmDiscard ? (
          <>
            <span className="muted">Throw these changes away for good?</span>
            <button type="button" onClick={() => setConfirmDiscard(false)}>No</button>
            <button type="button" className="danger" onClick={() => void onDiscard()}>Yes, throw away</button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setConfirmDiscard(true)}>No, use the saved plan</button>
            <button type="button" className="primary" onClick={onRestore}>Restore my changes</button>
          </>
        )}
      </div>
    </Dialog>
  );
}
